import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { Kids } from "@/components/Kids";
import { KidsTasterCTA } from "@/components/KidsTasterCTA";
import { PathwayBadge } from "@/components/PathwayBadge";
import { WeeklyTimetable } from "@/components/WeeklyTimetable";
import { RelatedLinks } from "@/components/RelatedLinks";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, ClipboardEdit } from "lucide-react";

export default function KidsPage() {
  usePageMeta(
    "USDD Kids",
    "USDD Kids — premium urban dance training for ages 6–17. Confidence, creativity, discipline, performances, showcases and competitions in a safe, structured environment."
  );
  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="03 · USDD Kids"
          title="Big energy."
          highlight="Tiny dancers."
          description="USDD Kids is our junior and youth programme for ages 6–17 — real street dance training delivered safely and seriously. Confidence, creativity, discipline, performances, showcases and competitions, all under one roof."
          ctaPrimary={{ label: "Register your child", to: "/kids-registration" }}
          ctaSecondary={{ label: "Book a £5 Taster", to: "/kids-registration?intent=taster" }}
          bg={SITE.kidsShow}
        />
        <Kids />
        <KidsTasterCTA />

        {/* Kids-only weekly timetable */}
        <section data-testid="kids-timetable-section" className="relative py-20 sm:py-24 bg-black overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-brand-purpleDeep/20 blur-3xl" />
            <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] rounded-full bg-brand-yellow/8 blur-3xl" />
          </div>
          <div className="relative max-w-6xl mx-auto px-6 sm:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Calendar size={16} className="text-brand-yellow" />
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">
                    Kids Weekly Timetable
                  </span>
                </div>
                <h2 className="font-display text-4xl sm:text-5xl text-white tracking-wide leading-[0.95]">
                  Pick a session. <span className="text-brand-yellow text-glow-yellow">Book a trial.</span>
                </h2>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/kids-registration"
                  data-testid="kids-page-register-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-[0.2em] hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow"
                >
                  <ClipboardEdit size={14} /> Register
                </Link>
                <Link
                  to="/kids-registration?intent=taster"
                  data-testid="kids-page-trial-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white font-bold uppercase text-xs tracking-[0.2em] hover:border-brand-yellow hover:text-brand-yellow transition-all"
                >
                  £5 Taster <ArrowRight size={14} />
                </Link>
              </div>
            </div>
            <WeeklyTimetable variant="compact" defaultFilter="kids" />
          </div>
        </section>

        <PathwayBadge testId="kids-pathway-badge" />
        <RelatedLinks
          title="Related"
          items={[
            { tag: "Adults", label: "Adult Weekly Training", desc: "For dancers 18+", to: "/training" },
            { tag: "Events", label: "Events & Battles", desc: "Junior showcases & comps", to: "/events" },
            { tag: "Schools", label: "Schools & Organisations", desc: "Programmes for your school", to: "/schools-organisations" },
          ]}
        />
      </main>
      <Footer />
    </>
  );
}
